import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
  usePreviewJoin,
  selectLocalPeer,
  useHMSStore,
  selectIsLocalVideoEnabled,
  useAVToggle,
} from '@100mslive/react-sdk';
import {
  Loading,
  Button,
  StyledVideoTile,
  Video,
  Text,
  Input,
  Flex,
  Avatar,
  IconButton,
  useTheme,
  useBorderAudioLevel,
  styled,
  textEllipsis
} from '@100mslive/roomkit-react';
import {SettingsIcon} from '@100mslive/react-icons';
import {AudioVideoToggle} from './AudioVideoToggle';
import Settings from './Settings';
import {
  UserPreferencesKeys,
  useUserPreferences,
} from './hooks/useUserPreferences';
import useWindowDimensions from './windowDimension';

const defaultPreviewPreference = {
  name: '',
  isAudioMuted: false,
  isVideoMuted: false,
};

const Preview = ({
  token,
  onJoin,
  env,
  skipPreview,
  initialName,
  onNameChange,
  accountLogo
}: any) => {
  const [previewPreference, setPreviewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference
  );
  const [name, setName] = useState(initialName || previewPreference.name);
  const {isLocalAudioEnabled, isLocalVideoEnabled} = useAVToggle();
  const {enableJoin, preview, join} = usePreviewJoin({
    name,
    token,
    initEndpoint: env ? `https://${env}-init.100ms.live/init` : undefined,
    initialSettings: {
      isAudioMuted: true,
      isVideoMuted: true,
},
    // initialSettings: {
    //   isAudioMuted: skipPreview ? true : previewPreference.isAudioMuted,
    //   isVideoMuted: skipPreview ? true : previewPreference.isVideoMuted,
    // },
    captureNetworkQualityInPreview: true,
  });
  const { height, width } = useWindowDimensions();
  const [isMobileScreen] = [width <= 440];
  const [isSmallHeight] = [height <= 700];

  const savePreferenceAndJoin = useCallback(() => {
    setPreviewPreference({
      name,
      isAudioMuted: !isLocalAudioEnabled,
      isVideoMuted: !isLocalVideoEnabled,
    });
    join();
    onJoin && onJoin();
  }, [
    join,
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    name,
    setPreviewPreference,
    onJoin,
  ]);
  useEffect(() => {
    if (token) {
      if (skipPreview) {
        savePreferenceAndJoin();
      } else {
        preview();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, skipPreview]);

  return (
    <Container >
      <div style={{margin:20}}>
        {accountLogo && <img
          src={accountLogo}
          alt='logo'
          height='50px'
          width='100px'
          style={{
            objectFit: 'contain'
          }}
        />}
      </div>
      <PreviewTile name={name} isMobileScreen={isMobileScreen} />
      <Flex direction="column" align="center" style={{marginBottom: 120}}>
        <Text css={{mb: '1rem',mt: '1rem', fontSize: 16}}>Enter your display name</Text>
        <Flex
          as="form"
          direction={isSmallHeight ? 'row' : "column"}
          align="center"
          style={{width:275,justifyContent:'space-between',alignItems:'center'}}
          onSubmit={(e: any) => {
            e.preventDefault();
            onJoin();
            // join();
            // savePreferenceAndJoin();
          }}>
          <Input
            css={{mb: !isSmallHeight && '1rem',backgroundColor:'white',color:'Black',width:'400'}}
            autoComplete="name"
            type="text"
            required
            autoFocus
            maxLength={20}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              onNameChange(e.target.value);
            }}
            data-testid="preview_name_field"
          />
          <Button
            type="submit"
            disabled={!name}
            data-testid="preview_join_btn"
            onClick={(e) => {
              e.preventDefault();
              onJoin();
            }}>
              {/* <SettingIcon /> */}
            Join
          </Button>
        </Flex>
      </Flex>
    </Container>
  );
};

const PreviewTile = ({name,isMobileScreen}: any) => {
  const localPeer = useHMSStore(selectLocalPeer);
  const borderAudioRef = useBorderAudioLevel(localPeer?.audioTrack);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const {
    aspectRatio: {width, height},
  } = useTheme();
  return (
    <StyledVideoTile.Container
      css={{
        aspectRatio: width / height,
        width: "unset",
        height: "min(360px, 60vh)",
        "@sm": {
          height: "unset",
          width: "min(360px, 90%)",
          maxWidth: "90%",
        },
      }}
      ref={borderAudioRef}
    >
      {localPeer || true ? (
        <>
          {/* <ConnectionIndicator isTile peerId={localPeer.id} /> */}
          <Video
            mirror={true}
            trackId={localPeer?.videoTrack}
            data-testid="preview_tile"
          />
          {!isVideoOn ? (
            <StyledVideoTile.AvatarContainer>
              <Avatar name={name} data-testid="preview_avatar_tile" />
              <Text css={{ ...textEllipsis("75%") }} variant="body2">
                {name}
              </Text>
            </StyledVideoTile.AvatarContainer>
          ) : null}

          <StyledVideoTile.AttributeBox css={actionBackgroundStyle}>
            <StyledVideoTile.AttributeBox css={controlStyles}>
              <AudioVideoToggle compact showText={true} />
            </StyledVideoTile.AttributeBox>
          </StyledVideoTile.AttributeBox>
          <StyledVideoTile.AttributeBox
            css={{ bottom: 14, marginLeft: isMobileScreen ? 70 : 80 }}
          >
            <div className="vl"></div>
          </StyledVideoTile.AttributeBox>
          <Settings>
            <StyledVideoTile.AttributeBox
              css={{ bottom: "12px", right: isMobileScreen ? "80px" : "120px" }}
            >
              {/* <IconButton data-testid="preview_setting_btn"> */}
              <div
                data-testid="preview_setting_btn"
                style={{ marginBottom: 2, cursor: "pointer" }}
              >
                <SettingsIcon />
                <div style={{ fontSize: 12 }}>Setting</div>
              </div>
              {/* </IconButton> */}
            </StyledVideoTile.AttributeBox>
          </Settings>
        </>
      ) : (
        <Loading size={100} />
      )}
    </StyledVideoTile.Container>
  );
};

const Container: any = styled('div', {
  borderRadius: '$2',
  backgroundColor: '$previewBg',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 6rem',
  '@md': {
    borderRadius: '0',
    padding: '0',
    width: '100%',
    height: '100%',
  },
  overflow: 'scroll',
} as any);

const controlStyles = {
  bottom: '10px',
  left: '42%',
  transform: 'translate(-50%, 0)',
  display: 'flex',
  '& > * + *': {
    marginRight: '0',
    marginLeft: '0.5rem',
  },
};

const actionBackgroundStyle = {
  backgroundColor:'#000000',
  height:70,
  width:'100%',
  bottom: '0px',
  borderBottomRightRadius: 10,
  borderBottomLeftRadius: 10,
};

export default Preview;
